import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { Inmate } from '@reliance/types/models/Inmate';
import { InmateSupportAssignedToType } from '@reliance/types/enums/InmateSupportAssignedToType';
import api from '../../../common/api';
import { useAlerts } from '../../../alert/AlertContext';

type SupportMessageSendProps = {
  inmate?: Inmate;
  message?: { message?: string };
  onMessageSent?: () => void;
};

const SupportMessageSend: React.FC<SupportMessageSendProps> = (props) => {
  const { inmate, message, onMessageSent } = props;
  const alerts = useAlerts();
  const [messageText, setMessageText] = useState<string>(message?.message || '');
  const [assignedToType, setAssignedToType] = useState<InmateSupportAssignedToType | null>(null);
  const [sending, setSending] = useState<boolean>(false);

  // Sync with external message prop changes
  useEffect(() => {
    if (message?.message !== undefined) {
      setMessageText(message.message);

      // Reset assignment when message is cleared externally
      if (!message.message) {
        setAssignedToType(null);
      }
    }
  }, [message?.message]);

  const handleSend = async () => {
    if (!messageText.trim() || !assignedToType || !inmate || sending) {
      return;
    }

    setSending(true);
    try {
      const requestData: any = {
        message: messageText,
        inmateId: inmate.id
      };

      if (assignedToType !== undefined) {
        requestData.assignedToType = assignedToType;
      }

      await api.post('/text-message/staff-message', requestData);

      alerts.success('The message was sent successfully');

      // Clear message text and reset assignment after successful send
      setMessageText('');
      setAssignedToType(null);

      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        alerts.error('The inmate no longer has a device assigned to receive this message');
      } else {
        alerts.error('An error occurred while sending the message');
      }
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSend();
  };

  const isFormValid = messageText.trim().length > 0 && assignedToType !== null;
  const isDisabled = !isFormValid || sending;
  const hasError = !isFormValid && (messageText.length > 0 || assignedToType !== null);

  if (!inmate) {
    return null;
  }

  const fullName = `${inmate.firstName} ${inmate.lastName}`;

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-horizontal card card-info">
        <div className="card-header">
          Send a Message to {fullName}
        </div>
        <div className="card-body">
          <div className="form-group">
            <div className={`col-md-6 ${hasError ? 'has-error' : ''}`}>
              <textarea
                rows={2}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder=""
                className="form-control"
                required
                disabled={sending}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-md-2 control-label">Assign To:</label>
            <div className="col-md-6">
              <ButtonGroup>
                <Button
                  type="button"
                  variant={assignedToType === InmateSupportAssignedToType.STAFF ? 'warning' : 'outline-warning'}
                  onClick={() => setAssignedToType(InmateSupportAssignedToType.STAFF)}
                  disabled={sending}>
                  <i className="fas fa-id-badge me-2"></i>
                  <strong>Staff</strong>
                </Button>
                <Button
                  type="button"
                  variant={assignedToType === InmateSupportAssignedToType.AI ? 'info' : 'outline-info'}
                  onClick={() => setAssignedToType(InmateSupportAssignedToType.AI)}
                  disabled={sending}>
                  <i className="fas fa-robot me-2"></i>
                  <strong>AI</strong>
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </div>
        <div className="card-footer">
          <Button
            type="submit"
            variant="primary"
            disabled={isDisabled}>
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default SupportMessageSend;
